import {
  Box,
  Button,
  Grid,
  InputAdornment,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import CommonCheckbox from "common/CheckBox/Checkbox";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useAuthService } from "subModule/src/services/userProfile/useProfile";
import AlertMessages from "common/Alert/AlertMessages";
import { useDispatch, useSelector } from "react-redux";
import { setUserInfo } from "features/user/userSlice";
import { brandingInfo } from "features/server/brandingSlice";
import { themes } from "theme/themes";
import { NotificationProfileService } from "subModule/src/core/services";
import { ActionsTypesResponse } from "subModule/src/services/Notifications/interface";
import { setActionTypes } from "features/user/actionSlice";

interface FormData {
  [key: string]: any;
}
export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const isScreen600pxOrSmaller = useMediaQuery("(max-width:600px)");
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<FormData>({ defaultValues: {} });
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(false);
  const onSubmit = (data: any) => {
    setIsBtnEnabled(true);
    onLogIn(data.email, data.password);
  };
  const { onLogIn } = useAuthService({
    onSuccess: (response) => {
      if (response.success) {
        dispatch(setUserInfo(response?.data as any));
        localStorage.setItem("selectedSubItem", "analytics");
        navigate("/analytics");
        fetchActionTypes();
      } else {
        enqueueSnackbar(<AlertMessages text={t(response.message)} />, {
          variant: "error",
        });
        setIsBtnEnabled(false);
      }
    },
    onError: (err: any) => {
      if (err) {
        const errorMessage = err?.data?.message || "An error occurred";
        enqueueSnackbar(errorMessage, { variant: "error" });
      }
      setIsBtnEnabled(false);
    },
  });
  const fetchActionTypes = () => {
    NotificationProfileService.getActionsType()
      .then((res: ActionsTypesResponse) => {
        dispatch(
          setActionTypes(
            res || {
              actions: {},
              types: {},
            },
          ),
        );
      })
      .catch((err: any) => {
        dispatch(
          setActionTypes({
            actions: {},
            types: {},
          }),
        );
        console.error("===", err);
        enqueueSnackbar(
          <AlertMessages text={err?.data?.message || "Something went wrong"} />,
          { variant: "error" },
        );
      });
  };
  const hasLoginBg =
    !!branding?.attributes?.logInPageImage &&
    (branding.attributes.logInPageImage.startsWith("blob:") ||
      branding.attributes.logInPageImage.startsWith("data:") ||
      branding.attributes.logInPageImage.startsWith("http"));

  return (
    <Box
      className={`flex items-center justify-end ${isScreen600pxOrSmaller ? "px-0" : "px-[80px]"}`}
      sx={{
        position: "relative",
        width: "100%",
        height: "100dvh",
        minHeight: "100vh",
        maxHeight: "100dvh",
        overflow: "hidden",
        boxSizing: "border-box",
        backgroundColor: themeColors?.appBg || "#F4F6F8",
      }}
    >
      {hasLoginBg ? (
        <Box
          component="img"
          src={branding!.attributes!.logInPageImage}
          alt=""
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "fill",
            objectPosition: "center center",
            pointerEvents: "none",
            userSelect: "none",
            zIndex: 0,
          }}
        />
      ) : null}
      <Grid
        container
        component="form"
        id="form"
        justifyContent="flex-end"
        alignItems="center"
        rowSpacing={2}
        onSubmit={handleSubmit(onSubmit)}
        onKeyDown={(e) => e.key === "Enter" && handleSubmit(onSubmit)()}
        className={`rounded-[8px] ${isScreen600pxOrSmaller ? "p-5" : "px-7 py-10"} ${isScreen600pxOrSmaller ? "w-[100%]" : "w-[390px]"}`}
        style={{ background: themeColors?.cardBg, position: "relative", zIndex: 1 }}
      >
        <Grid item xs={12}>
          {branding?.attributes?.logo?.startsWith("blob:") ||
          branding?.attributes?.logo?.startsWith("data:") ||
          branding?.attributes?.logo?.startsWith("http") ? (
            <img
              src={branding.attributes.logo}
              alt=""
              className="mx-auto w-[140px] h-auto max-h-[80px] object-contain"
            />
          ) : null}
        </Grid>
        <Grid item xs={12}>
          <TextField
            {...register("email", {
              validate: {
                required: (value) =>
                  value.trim() !== "" || t("This field is required."),
              },
            })}
            label={t("email") + " *"}
            placeholder={t("email")}
            title={t("email")}
            fullWidth
            error={!!errors.email}
            helperText={errors.email?.message as string}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon
                    style={{
                      width: "20px",
                      height: "20px",
                      fill: themeColors?.primary,
                    }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": { paddingRight: "0px !important" },
              "& .MuiFormHelperText-root": {
                position: "static",
                margin: "4px 0 0 0",
              },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            type={watch("showCurrentPassword") ? "text" : "password"}
            {...register("password", {
              required: t("This field is required."),
            })}
            label={t("password") + " *"}
            placeholder={t("password")}
            title={t("password")}
            fullWidth
            error={!!errors.password}
            helperText={errors.password?.message as string}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon
                    style={{
                      width: "20px",
                      height: "20px",
                      fill: themeColors?.primary,
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment: watch("password") ? (
                <InputAdornment position="end" className="cursor-pointer">
                  {watch("showCurrentPassword") ? (
                    <VisibilityIcon
                      style={{ fill: themeColors?.primary }}
                      onClick={() => setValue("showCurrentPassword", false)}
                    />
                  ) : (
                    <VisibilityOffIcon
                      style={{ fill: themeColors?.primary }}
                      onClick={() => setValue("showCurrentPassword", true)}
                    />
                  )}
                </InputAdornment>
              ) : null,
            }}
            sx={{
              "& .MuiFormHelperText-root": {
                position: "static",
                margin: "4px 0 0 0",
              },
            }}
          />
        </Grid>
        <Grid item xs={12} className="pt-0">
          <Controller
            name="rememberMe"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <CommonCheckbox
                {...field}
                label={t("rememberMe")}
                alignWithFieldLabel={false}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  field.onChange(event.target.checked)
                }
                checked={field.value}
                color={themeColors?.primary}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} className="pt-0">
          <Button
            type="submit"
            disabled={IsBtnEnabled}
            className="primary"
            style={{ background: themeColors?.primary }}
            variant="contained"
            disableElevation
            onClick={() => onSubmit}
          >
            {t("Log In")}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}
